import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";
import { Color } from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import ImageResize from "tiptap-extension-resize-image";
import { Extension, Node, mergeAttributes } from "@tiptap/core";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { uploadSetResource } from "../services/masterDataApi";
import {
  BsTypeBold,
  BsTypeItalic,
  BsTypeUnderline,
  BsListUl,
  BsListOl,
  BsListCheck,
  BsLink45Deg,
  BsArrowCounterclockwise,
  BsArrowClockwise,
  BsImage,
  BsTrash3,
  BsPalette,
  BsHighlighter,
  BsChevronDown,
  BsTextLeft,
  BsTextCenter,
  BsTextRight,
  BsJustify,
} from "react-icons/bs";

/* ── Custom FontSize mark ──────────────────────────────────────
   @tiptap/extension-text-style has no built-in font-size support;
   this is the standard documented pattern — extend TextStyle's
   attributes and add setFontSize/unsetFontSize commands. */
const FontSize = Extension.create({
  name: "fontSize",
  addOptions() {
    return { types: ["textStyle"] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => element.style.fontSize || null,
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px"];
const HEADING_OPTIONS = [
  { label: "Normal text", level: 0 },
  { label: "Heading 1", level: 1 },
  { label: "Heading 2", level: 2 },
  { label: "Heading 3", level: 3 },
];

/* ── Custom "BoxHighlight" node — wraps a paragraph (or more) in a bordered box ──
   This used to be a Mark rendering an inline <span>. The mobile app's HTML
   renderer (Flutter) drops a <span>'s styling and shows plain text — <div> is
   the tag it actually respects. A Mark can only ever render inline content
   (browsers themselves silently break a <div> nested inside a <p>, which is
   what a "div mark" would produce), so this has to be a block-level Node
   instead, toggled with toggleWrap like blockquote — it wraps whichever whole
   paragraph(s) the selection touches, not an arbitrary text run within one. */
export const BoxHighlight = Node.create({
  name: "boxHighlight",
  group: "block",
  content: "block+",
  defining: true,
  parseHTML() {
    return [
      { tag: "div[data-box-highlight]" },
      // Content saved before this change used an inline <span> mark — still
      // recognised here so existing boxed text keeps working once reopened,
      // though re-saving rewrites it to the new <div> form.
      { tag: "span[data-box-highlight]" },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-box-highlight": "",
        style:
          "background:#fef3c7;border:1.5px solid #f59e0b;border-radius:4px;padding:6px 10px;font-weight:600;",
      }),
      0,
    ];
  },
  addCommands() {
    return {
      toggleBoxHighlight:
        () =>
        ({ commands }) =>
          commands.toggleWrap(this.name),
    };
  },
});

/* ── Toolbar button ──────────────────────────────────────────── */
function TBBtn({ onClick, active, title, children, disabled }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${
        active
          ? "bg-[#0078d4]/10 text-[#0078d4]"
          : "text-[#57606a] hover:bg-gray-100"
      }`}
    >
      {children}
    </button>
  );
}

/* ── Simple preset color-swatch dropdown (text color / highlight color) ──
   Deliberately NOT a native <input type="color"> — that opens the OS's
   full RGB picker, which is overkill for this use case. This is a small
   click-to-pick palette instead, closed on outside click. */
function ColorPickerControl({ icon, title, colors, active, onPick, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <TBBtn onClick={() => setOpen((o) => !o)} active={active} title={title}>
        {icon}
      </TBBtn>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg p-2 flex flex-wrap gap-1.5 w-[144px]">
          {colors.map((c) => (
            <button
              key={c}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); onPick(c); setOpen(false); }}
              className="w-5 h-5 rounded border border-gray-300 hover:scale-110 transition-transform cursor-pointer"
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onClear(); setOpen(false); }}
            className="w-5 h-5 rounded border border-gray-300 flex items-center justify-center text-[10px] text-gray-500 bg-white hover:bg-gray-50 cursor-pointer"
            title="Clear"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

const TEXT_COLORS = ["#000000", "#495057", "#e03131", "#f08c00", "#2f9e44", "#1971c2", "#9c36b5", "#e64980"];
const HIGHLIGHT_COLORS = ["#fff3bf", "#ffe8cc", "#d3f9d8", "#d0ebff", "#eebefa", "#ffdeeb", "#e9ecef"];

/* ── List type dropdown — Bullet / Ordered / Task ── */
function ListDropdownControl({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const isActive = editor.isActive("bulletList") || editor.isActive("orderedList") || editor.isActive("taskList");

  const options = [
    { key: "bulletList", label: "Bullet List", icon: <BsListUl className="text-sm" />, run: () => editor.chain().focus().toggleBulletList().run() },
    { key: "orderedList", label: "Ordered List", icon: <BsListOl className="text-sm" />, run: () => editor.chain().focus().toggleOrderedList().run() },
    { key: "taskList", label: "Task List", icon: <BsListCheck className="text-sm" />, run: () => editor.chain().focus().toggleTaskList().run() },
  ];

  return (
    <div className="relative" ref={ref}>
      <TBBtn onClick={() => setOpen((o) => !o)} active={isActive} title="List type">
        <span className="flex items-center gap-0.5">
          <BsListUl className="text-sm" />
          <BsChevronDown className="text-[9px]" />
        </span>
      </TBBtn>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1 w-[160px]">
          {options.map((o) => (
            <button
              key={o.key}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); o.run(); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left cursor-pointer ${
                editor.isActive(o.key) ? "bg-[#0078d4]/10 text-[#0078d4]" : "text-[#24292f] hover:bg-gray-100"
              }`}
            >
              {o.icon}
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Heading dropdown — Normal text / H1 / H2 / H3 ── */
function HeadingDropdownControl({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = HEADING_OPTIONS.find((o) =>
    o.level === 0 ? !editor.isActive("heading") : editor.isActive("heading", { level: o.level })
  ) || HEADING_OPTIONS[0];

  const applyHeading = (level) => {
    if (level === 0) editor.chain().focus().setParagraph().run();
    else editor.chain().focus().toggleHeading({ level }).run();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        title="Text style"
        className="flex items-center gap-1 px-2 py-1.5 rounded text-xs text-[#57606a] hover:bg-gray-100 transition-colors min-w-[92px] justify-between"
      >
        <span>{current.label}</span>
        <BsChevronDown className="text-[9px]" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1 w-[160px]">
          {HEADING_OPTIONS.map((o) => (
            <button
              key={o.level}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); applyHeading(o.level); setOpen(false); }}
              className={`w-full flex items-center px-3 py-1.5 text-xs text-left cursor-pointer ${
                current.level === o.level ? "bg-[#0078d4]/10 text-[#0078d4]" : "text-[#24292f] hover:bg-gray-100"
              }`}
              style={o.level ? { fontSize: `${18 - (o.level - 1) * 2}px`, fontWeight: 600 } : undefined}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Font size dropdown ── */
function FontSizeDropdownControl({ editor }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const activeSize = editor.getAttributes("textStyle").fontSize || "";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); setOpen((o) => !o); }}
        title="Font size"
        className="flex items-center gap-1 px-2 py-1.5 rounded text-xs text-[#57606a] hover:bg-gray-100 transition-colors min-w-[64px] justify-between"
      >
        <span>{activeSize ? activeSize.replace("px", "") : "16"}</span>
        <BsChevronDown className="text-[9px]" />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1 w-[100px] max-h-[220px] overflow-y-auto">
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetFontSize().run(); setOpen(false); }}
            className={`w-full flex items-center px-3 py-1.5 text-xs text-left cursor-pointer ${
              !activeSize ? "bg-[#0078d4]/10 text-[#0078d4]" : "text-[#24292f] hover:bg-gray-100"
            }`}
          >
            Default
          </button>
          {FONT_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().setFontSize(size).run(); setOpen(false); }}
              className={`w-full flex items-center px-3 py-1.5 text-xs text-left cursor-pointer ${
                activeSize === size ? "bg-[#0078d4]/10 text-[#0078d4]" : "text-[#24292f] hover:bg-gray-100"
              }`}
            >
              {size.replace("px", "")}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Divider = () => <span className="w-px h-4 bg-gray-300 mx-1" />;

/* ── Image insertion ─────────────────────────────────────────────
   Images are uploaded to object storage and the document keeps only a short
   URL. They used to be inlined as base64 data URIs, which turned a few-KB
   save into a multi-MB one — big enough to stall at the gateway, so saving a
   cycle with an image in its Introduction just hung. */

export const MAX_EDITOR_IMAGE_BYTES = 5 * 1024 * 1024;

// Mirrors the allowlist the upload endpoint enforces (api/competency_set.py).
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
];

// Captures the data: URI out of an <img> in pasted HTML, so it can be uploaded
// and swapped for a real URL rather than travelling inside the document.
const PASTED_DATA_SRC_RE = /(<img\b[^>]*?\bsrc\s*=\s*)(["'])(data:image\/[^"']+)\2/gi;

/* Store the S3 URL the upload returns, exactly like the Set Images uploader
   (Sets.js handleResourceUpload) and the question-image uploader already do.
   It is presigned and expires, so every API response carrying this HTML
   re-signs the src through sloutils.object_storage.refresh_html_media — which
   also means the URL stays correct in whichever environment reads it, since
   the signature is regenerated against that environment's bucket and region. */
async function defaultUploadImage(file) {
  const res = await uploadSetResource(file);
  if (!res?.url) throw new Error("Upload failed — no URL returned");
  return res.url;
}

function validateImageFile(file) {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error(
      `"${file.type || "unknown"}" images aren't supported. Use JPG, PNG, GIF, WEBP or SVG.`
    );
  }
  if (file.size > MAX_EDITOR_IMAGE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const limit = MAX_EDITOR_IMAGE_BYTES / 1024 / 1024;
    throw new Error(`Image is ${mb} MB — the limit is ${limit} MB. Please compress it and try again.`);
  }
}

/* Copying an image out of a web page gives HTML with the bytes inline rather
   than a file. Upload each one and rewrite the src before the HTML reaches the
   document, so a paste behaves exactly like using the image button. */
async function uploadPastedDataImages(html, uploadImage) {
  const matches = [...html.matchAll(PASTED_DATA_SRC_RE)];
  if (!matches.length) return html;

  const replacements = await Promise.all(
    matches.map(async (m) => {
      const dataUri = m[3];
      const blob = await (await fetch(dataUri)).blob();
      const ext = (blob.type.split("/")[1] || "png").replace("+xml", "");
      const file = new File([blob], `pasted-${Date.now()}.${ext}`, { type: blob.type });
      validateImageFile(file);
      return [dataUri, await uploadImage(file)];
    })
  );

  return replacements.reduce(
    (acc, [dataUri, url]) => acc.split(dataUri).join(url),
    html
  );
}

const firstImageFile = (fileList) =>
  Array.from(fileList || []).find((f) => f.type?.startsWith("image/"));

/* FastAPI's `detail` is a string for HTTPException but a list of error objects
   for a validation failure — always end up with something renderable. */
function uploadErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message || "Image upload failed. Please try again.";
}

/* ── Toolbar ─────────────────────────────────────────────────── */
function Toolbar({ editor, roundedTop, onImageFile, imageUploading }) {
  const fileInputRef = useRef(null);
  if (!editor) return null;

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset first, so re-picking the same file still fires
    if (file) onImageFile(file);
  };

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", previousUrl || "https://");
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    // rounded-t applied directly here (not on a wrapping overflow-hidden div)
    // so the toolbar's own dropdown popups (color/highlight/list) — which
    // extend below this bar via position:absolute — don't get clipped.
    <div className={`flex flex-wrap items-center gap-0.5 px-2 py-1 border-b border-gray-200 bg-gray-50/50 ${roundedTop ? "rounded-t-lg" : ""}`}>
      <TBBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <BsArrowCounterclockwise className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <BsArrowClockwise className="text-sm" />
      </TBBtn>

      <Divider />

      <HeadingDropdownControl editor={editor} />
      <FontSizeDropdownControl editor={editor} />

      <Divider />

      <TBBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold">
        <BsTypeBold className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic">
        <BsTypeItalic className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline">
        <BsTypeUnderline className="text-sm" />
      </TBBtn>

      <Divider />

      <ColorPickerControl
        icon={<BsPalette className="text-sm" />}
        title="Text Color"
        colors={TEXT_COLORS}
        active={editor.isActive("textStyle") && !!editor.getAttributes("textStyle").color}
        onPick={(color) => editor.chain().focus().setColor(color).run()}
        onClear={() => editor.chain().focus().unsetColor().run()}
      />
      <ColorPickerControl
        icon={<BsHighlighter className="text-sm" />}
        title="Highlight Color"
        colors={HIGHLIGHT_COLORS}
        active={editor.isActive("highlight")}
        onPick={(color) => editor.chain().focus().setHighlight({ color }).run()}
        onClear={() => editor.chain().focus().unsetHighlight().run()}
      />

      <Divider />

      <ListDropdownControl editor={editor} />

      <Divider />

      <TBBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align Left">
        <BsTextLeft className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align Center">
        <BsTextCenter className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().setTextAlign("right").run()} active={editor.isActive({ textAlign: "right" })} title="Align Right">
        <BsTextRight className="text-sm" />
      </TBBtn>
      <TBBtn onClick={() => editor.chain().focus().setTextAlign("justify").run()} active={editor.isActive({ textAlign: "justify" })} title="Justify">
        <BsJustify className="text-sm" />
      </TBBtn>

      <Divider />

      <TBBtn onClick={setLink} active={editor.isActive("link")} title="Add/Edit Link (leave URL empty to remove)">
        <BsLink45Deg className="text-base" />
      </TBBtn>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_IMAGE_TYPES.join(",")}
        className="hidden"
        onChange={handleImageFile}
      />
      <TBBtn
        onClick={() => fileInputRef.current?.click()}
        disabled={imageUploading}
        title={
          imageUploading
            ? "Uploading image…"
            : "Insert Image (click the image afterward to resize/reposition it)"
        }
      >
        <BsImage className={`text-sm ${imageUploading ? "animate-pulse" : ""}`} />
      </TBBtn>
      {editor.isActive("imageResize") && (
        <TBBtn onClick={() => editor.chain().focus().deleteSelection().run()} title="Delete Image">
          <BsTrash3 className="text-sm text-red-500" />
        </TBBtn>
      )}

      <Divider />

      <TBBtn
        onClick={() => editor.chain().focus().toggleBoxHighlight().run()}
        active={editor.isActive("boxHighlight")}
        title="Wrap this paragraph in a box"
      >
        <span className="px-1 border border-amber-400 bg-amber-50 rounded text-[9px] leading-none font-semibold">Box</span>
      </TBBtn>
    </div>
  );
}

/* ── Read-only rendering helper ─────────────────────────────────
   tiptap-extension-resize-image persists each image's resized
   dimensions as non-standard `containerstyle="width: …"` /
   `wrapperstyle="…"` HTML attributes rather than a real `style=`
   attribute — it only applies them as actual CSS at runtime via a
   custom NodeView inside the live editor. Anywhere this editor's
   saved HTML is displayed via dangerouslySetInnerHTML (i.e. not
   through the editor itself), those attributes are inert and the
   image falls back to its natural (often huge) intrinsic size.
   Run saved HTML through this before rendering read-only to copy
   containerstyle onto a real style attribute. */
/* Tailwind's Preflight strips default list-style/margins from ul/ol and
   resets h1-h6 to inherit font-size/weight project-wide, and this app has
   no @tailwindcss/typography plugin registered (the "prose" classes used
   around this content are otherwise inert) — so lists and headings toggle
   correctly in the underlying HTML but render with no visible styling.
   Shared between the live editor and every read-only view of its content
   (via TiptapContentStyles + the "tiptap-content" class) so both match. */
const TIPTAP_CONTENT_CSS = `
  .tiptap-content ul:not([data-type="taskList"]) { list-style: disc !important; padding-left: 1.5rem !important; margin: 0.5rem 0 !important; }
  .tiptap-content ol { list-style: decimal !important; padding-left: 1.5rem !important; margin: 0.5rem 0 !important; }
  .tiptap-content li { display: list-item !important; margin: 0.15rem 0 !important; }
  .tiptap-content ul[data-type="taskList"] { list-style: none !important; padding-left: 0 !important; }
  .tiptap-content ul[data-type="taskList"] li { display: flex !important; align-items: flex-start !important; gap: 0.5rem !important; }
  .tiptap-content ul[data-type="taskList"] li > label { flex: 0 0 auto !important; user-select: none !important; margin-top: 0.2rem !important; }
  .tiptap-content ul[data-type="taskList"] li > div { flex: 1 1 auto !important; }
  .tiptap-content ul[data-type="taskList"] input[type="checkbox"] { cursor: pointer !important; }
  .tiptap-content h1 { font-size: 1.75rem !important; font-weight: 700 !important; line-height: 1.3 !important; margin: 0.75rem 0 0.5rem !important; }
  .tiptap-content h2 { font-size: 1.4rem !important; font-weight: 700 !important; line-height: 1.3 !important; margin: 0.65rem 0 0.4rem !important; }
  .tiptap-content h3 { font-size: 1.15rem !important; font-weight: 600 !important; line-height: 1.3 !important; margin: 0.55rem 0 0.35rem !important; }
`;

/* Drop this alongside any dangerouslySetInnerHTML render of saved editor
   content (with className="tiptap-content" on the wrapping element) so
   headings/lists match how they look inside the live editor. */
export function TiptapContentStyles() {
  return <style>{TIPTAP_CONTENT_CSS}</style>;
}

/* Callers render this inside .map() loops, so they can't wrap it in useMemo —
   the memoisation has to live here. Content saved before images moved to object
   storage still carries multi-MB base64 blobs, and parsing plus re-serialising
   one of those on every React render visibly locks the tab. */
const RENDER_CACHE = new Map();
const RENDER_CACHE_MAX = 24;

export function renderTiptapHtml(html) {
  if (!html) return html;
  // Nothing to rewrite: skip the parse entirely. This is the common case, and
  // it costs one substring scan instead of a full DOM round-trip.
  if (!html.includes("containerstyle")) return html;

  const cached = RENDER_CACHE.get(html);
  if (cached !== undefined) return cached;

  let result;
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("img[containerstyle]").forEach((img) => {
      const containerStyle = img.getAttribute("containerstyle");
      if (containerStyle) img.setAttribute("style", containerStyle);
    });
    result = doc.body.innerHTML;
  } catch {
    result = html;
  }

  // Bounded, insertion-ordered eviction — these strings can be large.
  if (RENDER_CACHE.size >= RENDER_CACHE_MAX) {
    RENDER_CACHE.delete(RENDER_CACHE.keys().next().value);
  }
  RENDER_CACHE.set(html, result);
  return result;
}

/* ── Main TiptapEditor ───────────────────────────────────────── */
export default function TiptapEditor({
  value = "",
  onChange,
  placeholder = "Type here…",
  minHeight = "160px",
  noBorder = false,
  // enableColors / enableTags kept for API compatibility but no longer affect toolbar
  // (a text-color and highlight-color picker are now always available in the toolbar)
  enableTags: _enableTags,
  enableColors: _enableColors,
  uploadImage = defaultUploadImage,
}) {
  const skipUpdate = useRef(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState("");
  // The paste/drop handlers are wired into the editor's own config, which is
  // built before these exist — reach them through refs.
  const insertImageRef = useRef(null);
  const pasteWithUploadsRef = useRef(null);

  const handleFileFromEvent = (files) => {
    const file = firstImageFile(files);
    if (!file) return false;
    insertImageRef.current?.(file);
    return true;
  };

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      // underline/link disabled here — StarterKit v3 now bundles its own copies
      // of both, which collided with our separately-configured Underline/Link
      // below ("Duplicate extension names found" warning); keep ours.
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, codeBlock: false, underline: false, link: false }),
      Underline,
      Placeholder.configure({ placeholder }),
      TextStyle,
      FontSize,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["paragraph", "heading"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      BoxHighlight,
      // allowBase64 stays true on purpose: content saved before images moved to
      // object storage still has data: URIs in it, and turning this off would
      // allowBase64 stays true: content saved before images moved to object
      // storage still has data: URIs in it, and turning this off would silently
      // drop those the moment someone opened the record. Fresh base64 does not
      // survive a save either way — pastes are uploaded below, and the API
      // converts anything that still arrives inline.
      ImageResize.configure({ inline: false, allowBase64: true, minWidth: 80, maxWidth: 800 }),
    ],
    content: value || "",
    editorProps: {
      // A pasted/dropped file is uploaded, exactly like using the toolbar button.
      handlePaste: (_view, event) => {
        if (handleFileFromEvent(event.clipboardData?.files)) return true;
        // Copying an image out of a web page yields HTML with the bytes inline
        // rather than a file. Upload those first, then insert.
        const html = event.clipboardData?.getData("text/html");
        if (!html || !html.toLowerCase().includes("data:image/")) return false;
        event.preventDefault();
        pasteWithUploadsRef.current?.(html);
        return true;
      },
      handleDrop: (_view, event) => handleFileFromEvent(event.dataTransfer?.files),
    },
    onUpdate: ({ editor: ed }) => {
      skipUpdate.current = true;
      const html = ed.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  const insertImage = useCallback(
    async (file) => {
      if (!editor || !file) return;
      setImageError("");
      try {
        validateImageFile(file);
      } catch (err) {
        setImageError(err.message);
        return;
      }
      setImageUploading(true);
      try {
        const src = await uploadImage(file);
        editor.chain().focus().setImage({ src }).run();
      } catch (err) {
        setImageError(uploadErrorMessage(err));
      } finally {
        setImageUploading(false);
      }
    },
    [editor, uploadImage]
  );

  const pasteWithUploads = useCallback(
    async (html) => {
      if (!editor) return;
      setImageError("");
      setImageUploading(true);
      try {
        editor.commands.insertContent(await uploadPastedDataImages(html, uploadImage));
      } catch (err) {
        // Nothing is inserted on failure — a half-pasted document with some
        // images missing would be worse than none.
        setImageError(uploadErrorMessage(err));
      } finally {
        setImageUploading(false);
      }
    },
    [editor, uploadImage]
  );

  insertImageRef.current = insertImage;
  pasteWithUploadsRef.current = pasteWithUploads;

  useEffect(() => {
    if (!editor) return;
    if (skipUpdate.current) { skipUpdate.current = false; return; }
    const current = editor.getHTML();
    const normalized = current === "<p></p>" ? "" : current;
    if (normalized !== (value || "")) editor.commands.setContent(value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  return (
    // NOTE: no overflow-hidden here (deliberately removed) — the image
    // resize/position controls render as overlay elements that can extend
    // slightly past the image's own box, and clipping the container hid
    // them / broke dragging near the edges.
    <div className={`${noBorder ? "" : "border border-gray-200 rounded-lg"} bg-white`}>
      <TiptapContentStyles />
      <Toolbar
        editor={editor}
        roundedTop={!noBorder}
        onImageFile={insertImage}
        imageUploading={imageUploading}
      />
      <EditorContent
        editor={editor}
        className="tiptap-content prose prose-sm max-w-none px-3 py-2"
        style={{ minHeight, overflow: "visible" }}
      />
      {imageUploading && (
        <div className="px-3 pb-2 text-xs text-gray-500">Uploading image…</div>
      )}
      {imageError && (
        <div className="flex items-start gap-2 px-3 pb-2 text-xs text-red-600">
          <span className="flex-1">{imageError}</span>
          <button
            type="button"
            onClick={() => setImageError("")}
            className="shrink-0 text-gray-400 hover:text-gray-600"
            title="Dismiss"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
